import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import identityService from '../../services/identityService';

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const DEFAULT_PAGE_SIZE = 10; // Default items per page

// Helper to generate cache key
const getCacheKey = (filters) => {
  return JSON.stringify({
    search: filters.search || '',
    type: filters.type,
    status: filters.status,
    riskLevel: filters.riskLevel,
    account_type: filters.account_type || '',
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    page: filters.page || 1,
    limit: filters.limit || DEFAULT_PAGE_SIZE,
  });
};

// Async thunk for fetching identities
export const fetchIdentities = createAsyncThunk(
  'identities/fetchIdentities',
  async (filters, { getState, rejectWithValue }) => {
    try {
      const state = getState().identities;
      const pageSize = filters.limit || DEFAULT_PAGE_SIZE;
      const page = filters.page || 1;
      const cacheKey = getCacheKey(filters);
      const now = Date.now();

      // Check if we have cached data for these filters
      const cachedData = state.cache[cacheKey];
      if (cachedData && now - cachedData.timestamp < CACHE_DURATION) {
        return {
          cached: true,
          cacheKey,
          data: cachedData.data,
        };
      }

      // Create query params only with non-null/non-undefined values
      const queryParams = {
        page,
        limit: pageSize,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      // Add optional filters only if they have values
      if (filters.search) queryParams.search = filters.search;
      if (filters.type) queryParams.type = filters.type;
      if (filters.status) queryParams.status = filters.status;
      if (filters.riskLevel) queryParams.riskLevel = filters.riskLevel;

      if (filters.account_type) {
        queryParams.account = filters.account_type;
      }

      const response = await identityService.getAllIdentities(queryParams);

      if (response.success) {
        const totalCount = response?.data?.count || 0;
        const totalPages = response?.data?.pages || 1;

        return {
          cached: false,
          cacheKey,
          data: {
            identities: response?.data?.identities || [],
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalCount: totalCount,
              totalPages: totalPages,
            },
            meta: response?.data?.meta || null,
          },
        };
      } else {
        return rejectWithValue(response?.error?.message || 'Failed to fetch identities');
      }
    } catch (error) {
      return rejectWithValue(error?.message || 'An error occurred');
    }
  }
);

// Async thunk for fetching MFA breakdown
export const fetchMfaBreakdown = createAsyncThunk(
  'identities/fetchMfaBreakdown',
  async (filters = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState().identities;
      const now = Date.now();

      const page = filters.page || 1;
      const limit = filters.limit || DEFAULT_PAGE_SIZE;

      const cacheKey = JSON.stringify({ page, limit });

      // Check cache
      const cached = state.mfa.cache?.[cacheKey];
      if (cached && now - cached.timestamp < CACHE_DURATION) {
        return {
          cached: true,
          cacheKey,
          data: cached.data,
        };
      }

      const response = await identityService.getMfaDistribution({ page, limit });
      if (!response.success) {
        return rejectWithValue(
          response?.error?.message || 'Failed to fetch MFA data'
        );
      }

      const data =
        response?.data?.data?.mfa_methods_distribution || [];

      return {
        cached: false,
        cacheKey,
        data,
        pagination: {
          currentPage: page,
          pageSize: limit,
          totalCount: response?.data?.data?.total || 0,
          totalPages: response?.data?.data?.pages || 1,
        },
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch MFA data');
    }
  }
);

//new additional mfa for users {} details..
export const fetchMfaMethodUsers = createAsyncThunk(
  'identities/fetchMfaMethodUsers',
  async ({ method, page = 1, limit = 10, search = '' }, { getState, rejectWithValue }) => {
    try {
      const state = getState().identities;
      const now = Date.now();
      // Include search in cache key so different searches are cached separately
      const cacheKey = `${method}-${page}-${limit}-${search}`;

      const cachedEntry = state.mfaMethodUsers.cache[cacheKey];
      if (cachedEntry && now - cachedEntry.timestamp < CACHE_DURATION) {
        return {
          cached: true,
          cacheKey,
          method,
          data: cachedEntry.data,
          pagination: cachedEntry.pagination,
        };
      }

      // Build query params - only add search if it has a value (same pattern as fetchIdentities)
      const queryParams = { page, limit };
      if (search) queryParams.search = search;

      const response = await identityService.getMfaMethodUsers(method, queryParams);

      if (!response.success) {
        return rejectWithValue(response?.error?.message || 'Failed to fetch MFA method users');
      }

      const payload = response?.data?.data || {};

      return {
        cached: false,
        cacheKey,
        method,
        data: payload,
        pagination: {
          currentPage: page,
          pageSize: limit,
          totalCount: payload.count || payload.total || 0,
          totalPages: payload.pages || 1,
        },
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Error fetching MFA method users');
    }
  }
);


// Async thunk for fetching identity remediation data
export const fetchIdentityRemediation = createAsyncThunk(
  'identities/fetchRemediation',
  async (identityId, { getState, rejectWithValue }) => {
    try {
      const state = getState().identities;
      const now = Date.now();

      // Check cache for this specific identity
      const cached = state.remediationCache[identityId];
      if (cached && now - cached.timestamp < CACHE_DURATION) {
        return {
          cached: true,
          identityId,
          data: cached.data,
        };
      }

      const response = await identityService.getSelectedIdentityAttackVectors(identityId);

      if (response.success) {
        return {
          cached: false,
          identityId,
          data: response.data,
        };
      } else {
        return rejectWithValue('Failed to fetch remediation data');
      }
    } catch (error) {
      return rejectWithValue(error?.message || 'An error occurred');
    }
  }
);


export const fetchIdentityDarkWebDetails = createAsyncThunk(
  'identities/fetchIdentityDarkWebDetails',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const page = filters.page || 1;
      const pageSize = filters.limit || DEFAULT_PAGE_SIZE;

      const queryParams = {
        page,
        limit: pageSize,
      };

      const response = await identityService.getIdentityDarkwebDetails(queryParams);
      // console.log("trsoutyoid", response?.data);

      if (response.success) {
        return {
          identities: response?.data?.data?.identities || [],
          pagination: {
            currentPage: page,
            pageSize: pageSize,
            totalCount: response?.data?.data?.total || 0,
            totalPages: response?.data?.data?.pages || 1,
          },
        };
      } else {
        return rejectWithValue('Failed to get identity darkweb data');
      }
    } catch (error) {
      return rejectWithValue(error?.message || 'An error occurred');
    }
  }
);

const identitiesSlice = createSlice({
  name: 'identities',
  initialState: {
    // Main identities data
    identities: [],
    pagination: {
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalCount: 0,
      totalPages: 1,
    },
    darkwebPagination: {
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalCount: 0,
      totalPages: 1,
    },
    meta: null,
    loading: false,
    error: null,

    // Filter state - WITH DEFAULT VALUES
    filters: {
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
      search: '',
      type: 'human', // Set default type to 'human'
      status: null,
      account_type: null,
      riskLevel: null,
      sortBy: 'displayName',
      sortOrder: 'asc',
    },

    // Cache storage
    cache: {}, // { cacheKey: { data, timestamp } }
    currentCacheKey: null,

    // MFA data
    mfa: {
      data: [],
      loading: false,
      error: null,
      lastFetched: null,
      cache: {},
      pagination: {
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalCount: 0,
        totalPages: 1,
      },
    },
    //mfaMethodUsers
    mfaMethodUsers: {
      data: [],
      loading: false,
      error: null,
      cache: {},
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 1,
      },
      lastFetched: null,
      selectedMethod: null,
      search: '',
    },

    darkwebIdentityDetails: {
      data: [],
      loading: false,
      error: null,
    },

    // Remediation cache
    remediationCache: {}, // { identityId: { data, timestamp } }
    currentRemediation: {
      data: null,
      loading: false,
      error: null,
    },
  },
  reducers: {
    // Update filters
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        page: action.payload?.page !== undefined ? action.payload.page : 1,
      };
    },

    // Set search (reset to page 1)
    setSearch: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },

    // Set page
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },

    // Set page size/limit
    setPageSize: (state, action) => {
      state.filters.limit = action.payload;
      state.filters.page = 1; // Reset to page 1 when changing page size
    },

    // Set sort
    setSort: (state, action) => {
      state.filters.sortBy = action.payload.sortBy;
      state.filters.sortOrder = action.payload.sortOrder || 'asc';
      state.filters.page = 1; // Reset to first page on sort
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
        search: '',
        type: 'human', // Reset to human type
        status: null,
        riskLevel: null,
        account_type: null,
        sortBy: 'displayName',
        sortOrder: 'asc',
      };
    },

    // Clear all cache
    clearCache: (state) => {
      state.cache = {};
      state.currentCacheKey = null;
    },

    // Clear MFA cache
    clearMfaCache: (state) => {
      state.mfa.lastFetched = null;
    },

    clearMfaMethodUsersCache: (state) => {
      state.mfaMethodUsers = {};
      state.mfaMethodUsers.data = [];
      state.mfaMethodUsers.selectedMethod = null;
    },

    setMfaMethodUsersPage: (state, action) => {
      state.mfaMethodUsers.pagination.currentPage = action.payload;
    },

    setMfaMethodUsersPageSize: (state, action) => {
      state.mfaMethodUsers.pagination.pageSize = action.payload;
      state.mfaMethodUsers.pagination.currentPage = 1;
    },

    setMfaMethodUsersSearch: (state, action) => {
      state.mfaMethodUsers.search = action.payload;
      state.mfaMethodUsers.pagination.currentPage = 1; // Reset to page 1 on new search
    },

    // Clear remediation cache
    clearRemediationCache: (state, action) => {
      if (action.payload) {
        delete state.remediationCache[action.payload];
      } else {
        state.remediationCache = {};
      }
    },

    // Clear current remediation
    clearCurrentRemediation: (state) => {
      state.currentRemediation = {
        data: null,
        loading: false,
        error: null,
      };
    },

    clearCurrentdarkwebDetails: (state) => {
      state.darkwebIdentityDetails = {
        data: null,
        loading: false,
        error: null,
      };
    },

  },

  extraReducers: (builder) => {
    builder
      // Fetch Identities
      .addCase(fetchIdentities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIdentities.fulfilled, (state, action) => {
        state.loading = false;
        const { cached, cacheKey, data } = action.payload;

        if (!cached) {
          // Store in cache
          state.cache[cacheKey] = {
            data,
            timestamp: Date.now(),
          };
        }

        state.currentCacheKey = cacheKey;
        state.identities = data.identities;
        state.pagination = data.pagination;
        state.meta = data.meta;
      })
      .addCase(fetchIdentities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.identities = [];
        state.pagination = {
          currentPage: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          totalCount: 0,
          totalPages: 1,
        };
      })

      // Fetch MFA Breakdown
      .addCase(fetchMfaBreakdown.pending, (state) => {
        state.mfa.loading = true;
        state.mfa.error = null;
      })
      .addCase(fetchMfaBreakdown.fulfilled, (state, action) => {
        state.mfa.loading = false;

        const { cached, cacheKey, data, pagination } = action.payload;

        if (!cached) {
          state.mfa.cache[cacheKey] = {
            data,
            pagination,
            timestamp: Date.now(),
          };
        }

        state.mfa.data = data;
        state.mfa.pagination = pagination;
        state.mfa.lastFetched = Date.now();
      })
      .addCase(fetchMfaBreakdown.rejected, (state, action) => {
        state.mfa.loading = false;
        state.mfa.error = action.payload;
      })

      //new mfa for user{} details
      .addCase(fetchMfaMethodUsers.pending, (state) => {
        state.mfaMethodUsers.loading = true;
        state.mfaMethodUsers.error = null;
      })
      .addCase(fetchMfaMethodUsers.fulfilled, (state, action) => {
        state.mfaMethodUsers.loading = false;

        const { cached, cacheKey, data, pagination, method } = action.payload;

        if (!cached) {
          state.mfaMethodUsers.cache[cacheKey] = {
            data,
            pagination,
            timestamp: Date.now(),
          };
        }

        state.mfaMethodUsers.data = data;
        state.mfaMethodUsers.pagination = pagination;
        state.mfaMethodUsers.selectedMethod = method;
      })
      .addCase(fetchMfaMethodUsers.rejected, (state, action) => {
        state.mfaMethodUsers.loading = false;
        state.mfaMethodUsers.error = action.payload;
      })

      // Fetch Remediation
      .addCase(fetchIdentityRemediation.pending, (state) => {
        state.currentRemediation.loading = true;
        state.currentRemediation.error = null;
      })
      .addCase(fetchIdentityRemediation.fulfilled, (state, action) => {
        state.currentRemediation.loading = false;
        const { cached, identityId, data } = action.payload;

        if (!cached) {
          state.remediationCache[identityId] = {
            data,
            timestamp: Date.now(),
          };
        }

        state.currentRemediation.data = data;
      })
      .addCase(fetchIdentityRemediation.rejected, (state, action) => {
        state.currentRemediation.loading = false;
        state.currentRemediation.error = action.payload;
      })

      .addCase(fetchIdentityDarkWebDetails.pending, (state) => {
        state.darkwebIdentityDetails.loading = true;
        state.darkwebIdentityDetails.error = null;
      })
      .addCase(fetchIdentityDarkWebDetails.fulfilled, (state, action) => {
        state.darkwebIdentityDetails.loading = false;
        state.darkwebIdentityDetails.data = action.payload.identities;
        state.darkwebPagination = action.payload.pagination;
      })
      .addCase(fetchIdentityDarkWebDetails.rejected, (state, action) => {
        state.darkwebIdentityDetails.loading = false;
        state.darkwebIdentityDetails.error = action.payload;
        state.darkwebIdentityDetails.data = [];
        state.darkwebPagination = {
          currentPage: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          totalCount: 0,
          totalPages: 1,
        };
      });
  },
});

export const {
  setFilters,
  setSearch,
  setPage,
  setPageSize,
  setSort,
  resetFilters,
  clearCache,
  clearMfaCache,
  clearMfaMethodUsersCache,
  setMfaMethodUsersPage,
  setMfaMethodUsersPageSize,
  setMfaMethodUsersSearch,
  clearRemediationCache,
  clearCurrentRemediation,
  clearCurrentdarkwebDetails,
} = identitiesSlice.actions;

export default identitiesSlice.reducer;